import type { ContactPoint, Organization, Thing, WebPage, WithContext } from 'schema-dts';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { composeSchemaGraph } from '@/shared/lib/utils/page-json-ld';
import {
    type SchemaContacts,
    buildSchemaOrganization,
    oneLine,
    schemaIdRef,
    stripHtml,
} from '@/shared/lib/utils/schema-common';
import { getSchemaIds, schemaPageUrl } from '@/shared/lib/utils/schema-ids';

export interface ContactData {
    phone?: string | null;
    email?: string | null;
    address?: {
        postalCode: string;
        city: string;
        street: string;
        location: string;
        name: string;
    };
}

interface ContactJsonLdOptions {
    h1?: string;
    description?: string | null;
    contactData?: ContactData;
    breadcrumbs?: AppBreadcrumbs | null;
}

export function generateContactJsonLd({
    h1,
    description,
    contactData,
    breadcrumbs,
}: ContactJsonLdOptions): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const pageUrl = schemaPageUrl('/contacts/');
    const contacts: SchemaContacts = {
        phone: contactData?.phone,
        email: contactData?.email,
        street: contactData?.address?.street,
        city: contactData?.address?.city,
        postalCode: contactData?.address?.postalCode,
    };
    const contactPoints: ContactPoint[] = [];
    if (contactData?.phone?.trim()) {
        contactPoints.push({
            '@type': 'ContactPoint',
            contactType: 'customer support',
            telephone: contactData.phone.trim(),
            availableLanguage: 'Russian',
        });
    }
    if (contactData?.email?.trim()) {
        contactPoints.push({
            '@type': 'ContactPoint',
            contactType: 'customer support',
            email: contactData.email.trim(),
            availableLanguage: 'Russian',
        });
    }
    const organization = buildSchemaOrganization(contacts) as WithContext<Organization> & {
        contactPoint?: ContactPoint[];
    };
    if (contactPoints.length > 0) {
        organization.contactPoint = contactPoints;
    }

    const descriptionText = description ? oneLine(stripHtml(description)) : '';
    const webPage: WithContext<WebPage> = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${pageUrl}#webpage`,
        name: oneLine(h1 || 'Контакты'),
        url: pageUrl,
        ...(descriptionText ? { description: descriptionText } : {}),
        isPartOf: schemaIdRef(ids.website) as WebPage['isPartOf'],
        publisher: schemaIdRef(ids.organization) as WebPage['publisher'],
    };

    return composeSchemaGraph({
        nodes: [organization as WithContext<Thing>, webPage as WithContext<Thing>],
        breadcrumbs,
    });
}
